"use client";

import { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import moment from "moment";
import dynamic from "next/dynamic";
import { motion } from "motion/react";
import { toast } from "react-toastify";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { AVATAR_CATEGORIES, AVATAR_GROUPS, randomAvatar } from "@/lib/avatar/catalog";
import { CUSTOM_CATEGORIES, pieceName, pieceOf, piecesOfCategory } from "@/lib/avatar/pieces";
import { showsWhenLocked } from "@/lib/avatar/unlockables";
import Button from "@/components/ui/Button/Button";
import AvatarCharacter from "./AvatarCharacter";
import usePieces from "./usePieces";

// La fenêtre de l'événement n'est téléchargée que si le membre l'ouvre depuis une pièce à gagner.
const EventModal = dynamic(() => import("@/components/AvatarEvent/EventModal"), { ssr: false });

// Ce qu'il faut faire pour débloquer une pièce, et où en est le membre.
// `entry` vient de /api/avatar/items (liste `locked`).
function UnlockPanel({ entry, name, onClaim, claiming, onOpenEvent }) {
    const t = useTranslations();
    const locale = useLocale();
    const day = (date) => moment(date, "YYYY-MM-DD HH:mm:ss").locale(locale).format(locale === "fr" ? "Do MMMM" : "MMMM D");
    const dates = entry.period ? { from: day(entry.period.from), to: day(entry.period.to) } : {};
    const closed = entry.state === "closed";

    let text;
    if (entry.type === "count") {
        const goal = t(`avatar_unlock_goal_${entry.metric}`, { target: entry.target });
        text = entry.period ? t(closed ? "avatar_unlock_count_closed" : "avatar_unlock_count_period", { goal, ...dates }) : t("avatar_unlock_count", { goal });
    } else if (entry.type === "badge") {
        text = t("avatar_unlock_badge", { badge: entry.badgeName?.[locale] || entry.badgeName?.fr || "" });
    } else if (entry.type === "event") {
        const event = entry.eventName?.[locale === "en" ? "en" : "fr"];
        text = event ? t("avatar_unlock_event", { event }) : t("avatar_unlock_event_generic");
    } else if (entry.type === "claim") {
        text = entry.period ? t(closed ? "avatar_unlock_claim_closed" : "avatar_unlock_claim_period", dates) : t("avatar_unlock_claim_text");
    } else {
        text = t("avatar_unlock_gift");
    }
    const progress = entry.type === "count" && !closed;

    return (
        <motion.div
            key={entry.item}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="mt-3 rounded-2xl bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-400/10 dark:text-blue-100"
        >
            <p className="mb-1 flex items-center gap-2 font-semibold">
                <SolarIcon name="lock-keyhole-minimalistic-linear" className="shrink-0 text-base" />
                {name}
            </p>
            <p className="mb-0">{text}</p>
            {progress && (
                <div className="mt-3 flex items-center gap-3">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-blue-200/70 dark:bg-white/10">
                        <motion.div
                            className="h-full rounded-full bg-[#613BDB]"
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.round((entry.count / entry.target) * 100)}%` }}
                            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                        />
                    </div>
                    <span className="shrink-0 text-xs font-medium tabular-nums">{t("avatar_unlock_progress", { count: entry.count, target: entry.target })}</span>
                </div>
            )}
            {entry.type === "event" && entry.state === "open" && (
                <div className="mt-3">
                    <Button small onClick={onOpenEvent}>
                        {t("avatar_unlock_event_cta")}
                    </Button>
                </div>
            )}
            {entry.type === "claim" && entry.state === "open" && (
                <div className="mt-3">
                    <Button small isLoading={claiming} onClick={() => onClaim(entry.item)}>
                        {t("avatar_unlock_claim")}
                    </Button>
                </div>
            )}
        </motion.div>
    );
}

// Écran de composition : l'aperçu en grand, quatre groupes (visage, cheveux,
// accessoires, tenue), puis une ligne par réglage dans le groupe ouvert.
export default function AvatarBuilder({ value, onChange }) {
    const t = useTranslations();
    const locale = useLocale();
    // Les pièces ajoutées sans redéploiement : elles complètent les pièces de base de leur catégorie.
    const pieces = usePieces();
    const nameOf = (item) => pieceName(pieces?.[item], locale);
    const [open, setOpen] = useState(AVATAR_GROUPS[0].key);
    const group = AVATAR_GROUPS.find((item) => item.key === open);

    // Les accessoires à débloquer : ceux du membre, et son avancement pour les autres.
    // Tant que la réponse n'est pas là, une pièce à débloquer reste verrouillée.
    const [unlocks, setUnlocks] = useState(null);
    const [inspected, setInspected] = useState(null);
    const [claiming, setClaiming] = useState(false);
    const [showEvent, setShowEvent] = useState(false);
    // Le nom vient de la réponse elle-même : les pièces ne sont pas forcément déjà chargées ici.
    const announce = (data) => (data.added || []).forEach((item) => toast.success(t("avatar_unlock_added", { name: data.names?.[item]?.[locale === "en" ? "en" : "fr"] || item })));
    useEffect(() => {
        let cancelled = false;
        fetch("/api/avatar/items")
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (cancelled || !data) return;
                setUnlocks(data);
                announce(data);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const owns = (item) => Boolean(unlocks?.owned?.includes(item));
    const claim = async (item) => {
        setClaiming(true);
        try {
            const response = await fetch("/api/avatar/items", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ item }),
            });
            if (!response.ok) throw new Error();
            setUnlocks(await response.json());
            setInspected(null);
            toast.success(t("avatar_unlock_added", { name: nameOf(item) }));
        } catch {
            toast.error(t("avatar_unlock_claim_error"));
        } finally {
            setClaiming(false);
        }
    };

    // Avatar proposé à partir d'une photo : elle part à l'analyse et n'est pas gardée.
    const fileInput = useRef(null);
    const [analysing, setAnalysing] = useState(false);
    // `request` : l'appel à lancer (photo envoyée, ou photo de profil). `source` dit lequel tourne.
    const propose = async (source, request) => {
        if (analysing) return;
        setAnalysing(source);
        try {
            const response = await request();
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.avatar) {
                // Une proposition tirée d'une photo n'a jamais de rides, quoi que réponde le serveur.
                onChange({ ...data.avatar, wrinkles: "none" });
            } else {
                const known = ["no_face", "too_large", "limit"].includes(data.code) ? data.code : "error";
                toast.error(t(`avatar_builder_photo_${known}`));
            }
        } catch {
            toast.error(t("avatar_builder_photo_error"));
        } finally {
            setAnalysing(false);
        }
    };
    const fromPhoto = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        propose("file", () => fetch("/api/avatar/from-photo", { method: "POST", body: form }));
    };
    // Le membre a déjà une photo de profil : BeBot peut partir d'elle, sans qu'il ait à la renvoyer.
    const { data: session } = useSession();
    const profilePhoto = session?.user?.photo;
    const hasProfilePhoto = typeof profilePhoto === "string" && profilePhoto !== "" && !profilePhoto.endsWith("default.png");
    const fromProfilePhoto = () =>
        propose("profile", () => fetch("/api/avatar/from-photo", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ profile: true }) }));

    return (
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
            <div className="flex flex-col items-center gap-4 sm:sticky sm:top-6">
                <AvatarCharacter avatar={value} size={176} animated interactive reactionLabel={t("avatar_builder_react")} />
                <button
                    type="button"
                    onClick={() => onChange(randomAvatar())}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-borderDark dark:text-gray-200 dark:hover:bg-white/5"
                >
                    <SolarIcon name="shuffle-linear" className="text-lg" />
                    {t("avatar_builder_random")}
                </button>
                <button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    disabled={Boolean(analysing)}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-borderDark dark:text-gray-200 dark:hover:bg-white/5"
                >
                    <SolarIcon name={analysing === "file" ? "refresh-linear" : "camera-linear"} className={`text-lg ${analysing === "file" ? "animate-spin" : ""}`} />
                    {t("avatar_builder_photo")}
                </button>
                {hasProfilePhoto && (
                    <button
                        type="button"
                        onClick={fromProfilePhoto}
                        disabled={Boolean(analysing)}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-borderDark dark:text-gray-200 dark:hover:bg-white/5"
                    >
                        {analysing === "profile" ? (
                            <SolarIcon name="refresh-linear" className="animate-spin text-lg" />
                        ) : (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={profilePhoto} alt="" className="h-5 w-5 rounded-full object-cover" />
                        )}
                        {t("avatar_builder_profile_photo")}
                    </button>
                )}
                <input ref={fileInput} type="file" accept="image/png, image/jpeg, image/webp" className="hidden" onChange={fromPhoto} />
                <p className="mb-0 max-w-[13rem] text-center text-xs text-gray-500 dark:text-gray-400">{t("avatar_builder_photo_note")}</p>
            </div>

            <div className="min-w-0 flex-1">
                {/* Dès sm, chaque onglet prend la largeur de son libellé : en quatre colonnes égales,
                    « Accessoires » manquait de place et son icône était écrasée. */}
                <div className="grid grid-cols-4 gap-2 sm:flex">
                    {AVATAR_GROUPS.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            onClick={() => setOpen(item.key)}
                            aria-pressed={item.key === open}
                            className={`flex min-w-0 flex-col items-center gap-1 whitespace-nowrap rounded-2xl px-2 py-3 text-xs font-medium transition-colors sm:flex-auto sm:flex-row sm:justify-center sm:gap-2 sm:px-3 sm:text-sm ${
                                item.key === open
                                    ? "bg-[#613BDB] text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
                            }`}
                        >
                            <SolarIcon name={item.icon} className="shrink-0 text-xl" />
                            {t(`avatar_builder_group_${item.key}`)}
                        </button>
                    ))}
                </div>

                <div className="flex flex-col gap-6 pt-6">
                    {group.categories.map((key) => {
                        const category = AVATAR_CATEGORIES.find((item) => item.key === key);
                        // Un réglage qui dépend d'un autre ne s'affiche que quand il a un effet.
                        const hidden = Object.entries(category.when || {}).some(([other, values]) => !values.includes(value[other]));
                        if (hidden) return null;
                        // La pièce verrouillée sur laquelle le membre vient de cliquer, si elle est dans cette ligne.
                        const inspectedEntry = inspected?.startsWith(`${key}:`) ? unlocks?.locked?.find((entry) => entry.item === inspected) : null;
                        return (
                            <div key={key}>
                                <p className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                                    {t(`avatar_builder_category_${key}`)}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {[...category.options, ...(CUSTOM_CATEGORIES.includes(key) ? piecesOfCategory(pieces, key).map((piece) => piece.item.split(":")[1]) : [])].map((option, index) => {
                                        const selected = value[key] === option;
                                        const swatch = category.swatches?.find((item) => item.id === option);
                                        // Une pièce ajoutée qui porte une condition se débloque ; sans condition, elle est libre.
                                        const added = pieceOf(pieces, key, option);
                                        const unlockable = added?.condition ? added : null;
                                        if (unlockable && !owns(unlockable.item)) {
                                            // Une pièce d'événement ne se montre que pendant son événement.
                                            const lockedEntry = unlocks?.locked?.find((entry) => entry.item === unlockable.item);
                                            if (!showsWhenLocked(unlockable, lockedEntry)) return null;
                                            // Verrouillée : on la voit, on ne la porte pas. Un clic dit comment l'obtenir.
                                            return (
                                                <button
                                                    key={option}
                                                    type="button"
                                                    onClick={() => setInspected(inspected === unlockable.item ? null : unlockable.item)}
                                                    aria-expanded={inspected === unlockable.item}
                                                    aria-label={t("avatar_unlock_locked_label", { name: nameOf(unlockable.item) })}
                                                    className={`relative flex h-16 w-16 items-center justify-center rounded-2xl border-2 transition-colors ${
                                                        inspected === unlockable.item
                                                            ? "border-blue-300 bg-blue-50 dark:border-blue-400/40 dark:bg-blue-400/10"
                                                            : "border-transparent bg-gray-50 hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10"
                                                    }`}
                                                >
                                                    <span className="opacity-50">
                                                        <AvatarCharacter avatar={{ ...value, [key]: option }} size={52} animated={false} />
                                                    </span>
                                                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-gray-600 shadow ring-1 ring-black/5 dark:bg-bgDark-800 dark:text-gray-200 dark:ring-white/10">
                                                        <SolarIcon name="lock-keyhole-minimalistic-bold" className="text-sm" />
                                                    </span>
                                                </button>
                                            );
                                        }
                                        // Une pièce gagnée garde un fond doré, pour la repérer parmi les pièces libres.
                                        const earned = Boolean(unlockable);
                                        return (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => onChange({ ...value, [key]: option })}
                                                aria-pressed={selected}
                                                aria-label={t("avatar_builder_option", {
                                                    category: t(`avatar_builder_category_${key}`),
                                                    number: index + 1,
                                                })}
                                                className={`flex items-center justify-center rounded-2xl border-2 transition-colors ${
                                                    swatch ? "h-12 w-12" : "h-16 w-16"
                                                } ${
                                                    selected
                                                        ? `border-[#613BDB] ${earned ? "bg-amber-100 dark:bg-amber-400/20" : "bg-[#EDE8FD] dark:bg-[#613BDB]/20"}`
                                                        : earned
                                                          ? "border-amber-200 bg-amber-50 hover:bg-amber-100 dark:border-amber-400/30 dark:bg-amber-400/10 dark:hover:bg-amber-400/20"
                                                          : "border-transparent bg-gray-50 hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10"
                                                }`}
                                            >
                                                {swatch ? (
                                                    <span
                                                        className="h-8 w-8 rounded-full ring-1 ring-black/10 dark:ring-white/20"
                                                        style={{ backgroundColor: swatch.fill }}
                                                    />
                                                ) : (
                                                    <AvatarCharacter
                                                        avatar={{ ...value, [key]: option }}
                                                        size={52}
                                                        animated={false}
                                                    />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                {inspectedEntry && <UnlockPanel entry={inspectedEntry} name={nameOf(inspectedEntry.item)} onClaim={claim} claiming={claiming} onOpenEvent={() => setShowEvent(true)} />}
                            </div>
                        );
                    })}
                </div>
            </div>
            {showEvent && <EventModal isOpen onClose={() => setShowEvent(false)} />}
        </div>
    );
}
